// The entitlement token, checked offline (plan §10.5). The station keeps the last token from
// `/devices/config` and the public keys from `/entitlements/keys` in `device_state`, and
// verifies with @enp/domain `verifyEntitlementToken`. An expired token never locks a shift
// out: the modules stay on and the station shows a banner.

import {
  type Ed25519Subtle,
  type EntitlementVerification,
  verifyEntitlementToken,
} from '@enp/domain';
import type { EntitlementJwk } from './api.js';
import { DEVICE_STATE_KEYS, readDeviceState } from './device-state.js';
import type { LocalReader } from './engine.js';

/** What `/devices/config` gave us, as stored. */
export interface StoredEntitlement {
  token: string;
  kid: string;
  expires_at: string;
  modules: string[];
}

export type EntitlementState =
  /** Nothing yet (a new station before its first config). Not a lock-out. */
  | { status: 'missing'; banner: false; modules: string[] }
  | { status: 'valid'; banner: false; modules: string[]; expiresAt: string }
  /** Past its date: keep working, show the banner (never lock out). */
  | { status: 'expired'; banner: true; modules: string[]; expiresAt: string }
  /** A token that doesn't verify (unknown key, bad signature). Shown, never enforced. */
  | { status: 'invalid'; banner: true; modules: string[]; reason: string };

/** base64url (no padding) → bytes. Throws on bad input. */
export function base64UrlToBytes(text: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) throw new RangeError('Not base64url');
  const b64 = text.replaceAll('-', '+').replaceAll('_', '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** `{ kid: raw public key (base64url) }` from the JWKs. */
export function entitlementKeysFromJwks(keys: readonly EntitlementJwk[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of keys) {
    if (k.kty === 'OKP' && k.crv === 'Ed25519' && typeof k.x === 'string') out[k.kid] = k.x;
  }
  return out;
}

export async function evaluateEntitlement(
  db: LocalReader,
  subtle: Ed25519Subtle,
  now: Date,
): Promise<EntitlementState> {
  const stored = await readDeviceState<StoredEntitlement>(db, DEVICE_STATE_KEYS.entitlement);
  if (!stored) return { status: 'missing', banner: false, modules: [] };
  const keys =
    (await readDeviceState<Record<string, string>>(db, DEVICE_STATE_KEYS.entitlementKeys)) ?? {};
  const publicKeys: Record<string, Uint8Array<ArrayBuffer>> = {};
  for (const [kid, x] of Object.entries(keys)) {
    try {
      publicKeys[kid] = base64UrlToBytes(x);
    } catch {
      // skip a key we can't read
    }
  }
  const result: EntitlementVerification = await verifyEntitlementToken(
    stored.token,
    publicKeys,
    subtle,
    now,
  );
  switch (result.status) {
    case 'valid':
      return {
        status: 'valid',
        banner: false,
        modules: result.payload.modules,
        expiresAt: result.payload.expires_at,
      };
    case 'expired':
      return {
        status: 'expired',
        banner: true,
        modules: result.payload.modules,
        expiresAt: result.payload.expires_at,
      };
    case 'invalid':
      // Degrade in the customer's favour: keep the modules the server last listed.
      return { status: 'invalid', banner: true, modules: stored.modules, reason: result.reason };
  }
}
